/**
 * Create Deployment — 4 steps: select release, targets, strategy, confirm.
 */
import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, AlertCircle } from 'lucide-react';
import { useGatewayFirmwareReleases } from '@/hooks/useGatewayFirmwareReleases';
import { useGatewayDevices } from '@/hooks/useGatewayDevices';
import { useGatewayDeployments } from '@/hooks/useGatewayDeployments';
import type { CreateDeploymentInput } from '@/services/gateway';
import type { GatewayFirmwareRelease } from '@/types/gateway';
import { format } from 'date-fns';

type Step = 1 | 2 | 3 | 4;

export interface CreateDeploymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (deploymentId: string) => void;
}

const STRATEGIES: { value: CreateDeploymentInput['strategy']; label: string }[] = [
  { value: 'immediate', label: 'Immediate (all targets queued at once)' },
  { value: 'staged', label: 'Staged (10% → 25% → 50% → 100%)' },
  { value: 'maintenance_window', label: 'Maintenance window (activate only during configured window)' },
];

export function CreateDeploymentDialog({ open, onOpenChange, onCreated }: CreateDeploymentDialogProps) {
  const [step, setStep] = useState<Step>(1);
  const [releaseId, setReleaseId] = useState<string>('');
  const [targetMode, setTargetMode] = useState<'all' | 'firmware' | 'specific'>('all');
  const [firmwareFilter, setFirmwareFilter] = useState<string>('');
  const [selectedGatewayIds, setSelectedGatewayIds] = useState<string[]>([]);
  const [strategy, setStrategy] = useState<CreateDeploymentInput['strategy']>('immediate');
  const [stagedStages, setStagedStages] = useState<string>('10, 25, 50, 100');
  const [autoAdvance, setAutoAdvance] = useState(false);
  const [healthCheckMinutes, setHealthCheckMinutes] = useState(30);
  const [maintenanceDays, setMaintenanceDays] = useState<string[]>([]);
  const [maintenanceStart, setMaintenanceStart] = useState('02:00');
  const [maintenanceEnd, setMaintenanceEnd] = useState('05:00');
  const [maintenanceTz, setMaintenanceTz] = useState('America/Chicago');
  const [name, setName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const { releases } = useGatewayFirmwareReleases();
  const { devices } = useGatewayDevices();
  const { createDeployment, isCreating, createError } = useGatewayDeployments();

  const releasedReleases = useMemo(
    () => (releases ?? []).filter((r: GatewayFirmwareRelease) => r.release_status === 'released'),
    [releases]
  );

  const selectedRelease = useMemo(
    () => releasedReleases.find((r) => r.id === releaseId),
    [releasedReleases, releaseId]
  );

  const targetCandidates = useMemo(() => {
    if (!selectedRelease) return [];
    let list = devices.filter((d) => d.lifecycle_status === 'active');
    if (targetMode === 'firmware' && firmwareFilter) {
      list = list.filter((d) => d.firmware_version === firmwareFilter);
    } else if (targetMode === 'specific' && selectedGatewayIds.length) {
      list = list.filter((d) => selectedGatewayIds.includes(d.id));
    }
    return list.filter((d) => d.firmware_version !== selectedRelease?.version);
  }, [devices, selectedRelease, targetMode, firmwareFilter, selectedGatewayIds]);

  const alreadyOnVersion = useMemo(() => {
    if (!selectedRelease) return 0;
    return devices.filter((d) => d.lifecycle_status === 'active' && d.firmware_version === selectedRelease.version).length;
  }, [devices, selectedRelease]);

  const handleCreate = async () => {
    setErrorMsg('');
    const input: CreateDeploymentInput = {
      firmware_release_id: releaseId,
      name: name.trim() || undefined,
      strategy,
      target_filter: { all: targetMode === 'all' },
    };
    if (targetMode === 'firmware' && firmwareFilter) {
      input.target_filter = { all: false, firmware_version: firmwareFilter };
    } else if (targetMode === 'specific' && selectedGatewayIds.length) {
      input.target_filter = { all: false, gateway_ids: selectedGatewayIds };
    }
    if (strategy === 'staged') {
      input.staged_config = {
        stages: stagedStages.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n)),
        auto_advance: autoAdvance,
        health_check_minutes: healthCheckMinutes,
      };
    } else if (strategy === 'maintenance_window') {
      input.maintenance_window = {
        days: maintenanceDays,
        start: maintenanceStart,
        end: maintenanceEnd,
        timezone: maintenanceTz,
      };
    }
    try {
      const { deployment_id } = await createDeployment(input);
      onCreated?.(deployment_id);
      onOpenChange(false);
      reset();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Failed to create deployment');
    }
  };

  function reset() {
    setStep(1);
    setReleaseId('');
    setTargetMode('all');
    setFirmwareFilter('');
    setSelectedGatewayIds([]);
    setStrategy('immediate');
    setStagedStages('10, 25, 50, 100');
    setAutoAdvance(false);
    setHealthCheckMinutes(30);
    setMaintenanceDays([]);
    setMaintenanceStart('02:00');
    setMaintenanceEnd('05:00');
    setMaintenanceTz('America/Chicago');
    setName('');
    setErrorMsg('');
  }

  const firmwareVersions = useMemo(() => {
    const set = new Set<string>();
    devices.forEach((d) => { if (d.firmware_version) set.add(d.firmware_version); });
    return Array.from(set).sort();
  }, [devices]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg bg-wt-bg-secondary border-border max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-wt-text-primary">
            {step === 4 ? 'Confirm deployment' : `Create deployment — Step ${step}`}
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <Label className="text-wt-text-secondary">Firmware release</Label>
            <Select value={releaseId || undefined} onValueChange={setReleaseId}>
              <SelectTrigger className="bg-wt-bg-tertiary border-border">
                <SelectValue placeholder="Select release" />
              </SelectTrigger>
              <SelectContent>
                {releasedReleases.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.version} — {r.released_at ? format(new Date(r.released_at), 'MMM d, yyyy') : 'No date'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedRelease?.release_notes && (
              <p className="text-xs text-wt-text-secondary whitespace-pre-wrap">{selectedRelease.release_notes}</p>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <RadioGroup value={targetMode} onValueChange={(v) => setTargetMode(v as 'all' | 'firmware' | 'specific')}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="t-all" />
                <Label htmlFor="t-all">All gateways</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="firmware" id="t-fw" />
                <Label htmlFor="t-fw">Filter by current firmware version</Label>
              </div>
              {targetMode === 'firmware' && (
                <Select value={firmwareFilter || undefined} onValueChange={setFirmwareFilter}>
                  <SelectTrigger className="w-full mt-2 bg-wt-bg-tertiary border-border">
                    <SelectValue placeholder="Version" />
                  </SelectTrigger>
                  <SelectContent>
                    {firmwareVersions.map((v) => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="specific" id="t-spec" />
                <Label htmlFor="t-spec">Select specific gateways</Label>
              </div>
              {targetMode === 'specific' && (
                <div className="mt-2 max-h-48 overflow-y-auto border border-border rounded p-2 space-y-1">
                  {devices.filter((d) => d.lifecycle_status === 'active').map((d) => (
                    <label key={d.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedGatewayIds.includes(d.id)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedGatewayIds((ids) => [...ids, d.id]);
                          else setSelectedGatewayIds((ids) => ids.filter((id) => id !== d.id));
                        }}
                      />
                      <span className="text-sm font-mono-numeric">{d.mac_address ?? d.id.slice(0, 8)}</span>
                      <span className="text-xs text-wt-text-secondary">{d.property_name ?? 'Unassigned'}</span>
                    </label>
                  ))}
                </div>
              )}
            </RadioGroup>
            <p className="text-sm text-wt-text-secondary">
              {targetCandidates.length} gateways will be updated.
              {alreadyOnVersion > 0 && ` ${alreadyOnVersion} already on this version (skipped).`}
            </p>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <RadioGroup value={strategy} onValueChange={(v) => setStrategy(v as CreateDeploymentInput['strategy'])}>
              {STRATEGIES.map((s) => (
                <div key={s.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={s.value} id={`s-${s.value}`} />
                  <Label htmlFor={`s-${s.value}`}>{s.label}</Label>
                </div>
              ))}
            </RadioGroup>
            {strategy === 'staged' && (
              <div className="pl-6 space-y-2">
                <Label className="text-xs">Stage percentages (comma-separated)</Label>
                <Input
                  value={stagedStages}
                  onChange={(e) => setStagedStages(e.target.value)}
                  className="font-mono-numeric"
                />
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={autoAdvance} onChange={(e) => setAutoAdvance(e.target.checked)} />
                  <span className="text-sm">Auto-advance to next stage</span>
                </label>
                <div>
                  <Label className="text-xs">Health check delay (minutes)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={healthCheckMinutes}
                    onChange={(e) => setHealthCheckMinutes(parseInt(e.target.value, 10) || 30)}
                  />
                </div>
              </div>
            )}
            {strategy === 'maintenance_window' && (
              <div className="pl-6 space-y-2">
                <Label className="text-xs">Days</Label>
                <div className="flex flex-wrap gap-2">
                  {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((day) => (
                    <label key={day} className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={maintenanceDays.includes(day)}
                        onChange={(e) => {
                          if (e.target.checked) setMaintenanceDays((d) => [...d, day]);
                          else setMaintenanceDays((d) => d.filter((x) => x !== day));
                        }}
                      />
                      <span className="text-sm capitalize">{day}</span>
                    </label>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Start</Label>
                    <Input type="time" value={maintenanceStart} onChange={(e) => setMaintenanceStart(e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">End</Label>
                    <Input type="time" value={maintenanceEnd} onChange={(e) => setMaintenanceEnd(e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Timezone</Label>
                  <Input value={maintenanceTz} onChange={(e) => setMaintenanceTz(e.target.value)} />
                </div>
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <div className="rounded bg-wt-bg-tertiary p-3 text-sm space-y-1">
              <p><span className="text-wt-text-secondary">Release:</span> {selectedRelease?.version}</p>
              <p><span className="text-wt-text-secondary">Targets:</span> {targetCandidates.length}</p>
              <p><span className="text-wt-text-secondary">Strategy:</span> {strategy}</p>
            </div>
            <div>
              <Label className="text-wt-text-secondary">Deployment name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. March 2026 update" className="mt-1" />
            </div>
            {errorMsg && (
              <p className="text-sm text-wt-red-primary flex items-center gap-1.5">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {errorMsg}
              </p>
            )}
            {createError && (
              <p className="text-sm text-wt-red-primary">{createError.message}</p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep((s) => (s - 1) as Step)}>
              Back
            </Button>
          )}
          {step < 4 ? (
            <Button
              onClick={() => setStep((s) => (s + 1) as Step)}
              disabled={
                (step === 1 && !releaseId) ||
                (step === 2 && targetCandidates.length === 0) ||
                (step === 3 && strategy === 'maintenance_window' && maintenanceDays.length === 0)
              }
            >
              Continue
            </Button>
          ) : (
            <Button onClick={handleCreate} disabled={isCreating || targetCandidates.length === 0}>
              {isCreating && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
              Create deployment
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
